import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Content from "../layout/content/Content";
import Head from "../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Row,
  Col,
  PreviewAltCard,
  TrendCard,
  PeriodFilter,
  BlockBetween,
  TopListWidget,
} from "../components/Component";
import http from "../utils/http-common";
import { getPublicUrl } from "../utils/Utils";
import moment from "moment";

const HomePage = () => {
  const [stats, setStats] = useState({
    users: { total: 0, previous: null, loading: true },
    quotes: { total: 0, previous: null, loading: true },
    clinics: { total: 0, previous: null, loading: true },
    partners: { total: 0, previous: null, loading: true },
    consultations: { total: 0, previous: 0, loading: true },
  });
  const [period, setPeriod] = useState("30d");
  const [tcStats, setTcStats] = useState({
    total: 0,
    previousTotal: 0,
    byStatus: { scheduled: 0, paid: 0, completed: 0, cancelled: 0, no_show: 0 },
    loading: true,
  });
  const [topClinics, setTopClinics] = useState({ items: [], loading: true });
  const [topPartners, setTopPartners] = useState({ items: [], loading: true });
  const history = useHistory();
  const isMountedRef = useRef(true);

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return Math.round(change * 10) / 10;
  };


  const fetchTopLists = useCallback(async () => {
    try {
      const clinicsRes = await http.get("/api/v1/partners/medical/clinics", {
        suppressServerErrorSwal: true,
      });
      const clinics = clinicsRes.data?.data || clinicsRes.data || [];
      
      if (isMountedRef.current) {
        setTopClinics({ 
          items: clinics.slice(0, 5).map((c) => ({
            name: c.name || c.clinicName || "Clinic",
            count: null,
          })), 
          loading: false 
        });
      }

      const partnersRes = await http.get("/api/v1/medical-partners", {
        suppressServerErrorSwal: true,
      });
      const partners = partnersRes.data?.data || partnersRes.data || [];
      
      if (isMountedRef.current) {
        setTopPartners({ 
          items: partners.slice(0, 5).map((p) => ({
            name: p.organization_name || "Partner",
            count: null,
          })), 
          loading: false 
        });
      }
    } catch (error) {
      console.error("Error fetching top lists:", error);
      if (isMountedRef.current) {
        setTopClinics({ items: [], loading: false });
        setTopPartners({ items: [], loading: false });
      }
    }
  }, []);

  const fetchStatistics = useCallback(async () => {
    // Fetch Users count
    try {
      const usersRes = await http.get("/api/v1/user/", {
        params: { limit: 1 },
        suppressServerErrorSwal: true,
      });
      if (isMountedRef.current) {
        const total = usersRes.data.pagination?.total || usersRes.data.count || 0;
        setStats((prev) => ({
          ...prev,
          users: { total, previous: null, loading: false },
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      if (isMountedRef.current) {
        setStats((prev) => ({ ...prev, users: { total: 0, previous: null, loading: false } }));
      }
    }

    // Fetch Quotes count
    try {
      const quotesRes = await http.get("/api/v1/quote/", {
        params: { limit: 1 },
        suppressServerErrorSwal: true,
      });
      if (isMountedRef.current) {
        const total = quotesRes.data.pagination?.total || quotesRes.data.count || 0;
        setStats((prev) => ({
          ...prev,
          quotes: { total, previous: null, loading: false },
        }));
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
      if (isMountedRef.current) {
        setStats((prev) => ({ ...prev, quotes: { total: 0, previous: null, loading: false } }));
      }
    }

    // Fetch Clinics count
    try {
      const clinicsRes = await http.get("/api/v1/partners/medical/clinics", {
        suppressServerErrorSwal: true,
      });
      if (isMountedRef.current) {
        const total = clinicsRes.data.count || clinicsRes.data.data?.length || 0;
        setStats((prev) => ({
          ...prev,
          clinics: { total, previous: total, loading: false },
        }));
      }
    } catch (error) {
      console.error("Error fetching clinics:", error);
      if (isMountedRef.current) {
        setStats((prev) => ({ ...prev, clinics: { total: 0, previous: null, loading: false } }));
      }
    }

    // Fetch Partners count
    try {
      const partnersRes = await http.get("/api/v1/medical-partners", {
        params: { limit: 1 },
        suppressServerErrorSwal: true,
      });
      if (isMountedRef.current) {
        const total = partnersRes.data.pagination?.total || partnersRes.data.count || partnersRes.data.data?.length || 0;
        setStats((prev) => ({
          ...prev,
          partners: { total, previous: null, loading: false },
        }));
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
      if (isMountedRef.current) {
        setStats((prev) => ({ ...prev, partners: { total: 0, previous: null, loading: false } }));
      }
    }

    // Fetch Teleconsultations (admin list)
    try {
      const consultationsRes = await http.get("/api/v1/consultations", {
        suppressServerErrorSwal: true,
      });
      if (isMountedRef.current) {
        const list = consultationsRes.data?.data || consultationsRes.data?.consultations || consultationsRes.data || [];
        const total = Array.isArray(list) ? list.length : 0;
        setStats((prev) => ({
          ...prev,
          consultations: { total, previous: 0, loading: false },
        }));
      }
    } catch (error) {
      console.error("Error fetching consultations:", error);
      if (isMountedRef.current) {
        setStats((prev) => ({ ...prev, consultations: { total: 0, previous: 0, loading: false } }));
      }
    }

    // Fetch Teleconsultation stats (period + previous period, by status)
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
    const dateTo = moment().endOf("day").toDate();
    const dateFrom = moment().subtract(days, "days").startOf("day").toDate();
    const prevDateTo = moment().subtract(days, "days").endOf("day").toDate();
    const prevDateFrom = moment().subtract(days * 2, "days").startOf("day").toDate();
    try {
      const [currentRes, previousRes] = await Promise.all([
        http.get("/api/v1/consultations", {
          params: { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString(), limit: 500 },
          suppressServerErrorSwal: true,
        }),
        http.get("/api/v1/consultations", {
          params: { dateFrom: prevDateFrom.toISOString(), dateTo: prevDateTo.toISOString(), limit: 1 },
          suppressServerErrorSwal: true,
        }),
      ]);
      if (isMountedRef.current) {
        const list = currentRes.data?.data || currentRes.data?.consultations || currentRes.data || [];
        const arr = Array.isArray(list) ? list : [];
        const total = currentRes.data?.total ?? arr.length;
        const previousTotal = previousRes.data?.total ?? 0;
        const byStatus = { scheduled: 0, paid: 0, completed: 0, cancelled: 0, no_show: 0 };
        arr.forEach((c) => {
          const s = (c.status || "").toLowerCase();
          if (byStatus.hasOwnProperty(s)) byStatus[s] += 1;
        });
        setTcStats({ total, previousTotal, byStatus, loading: false });
      }
    } catch (err) {
      console.error("Error fetching teleconsultation stats:", err);
      if (isMountedRef.current) {
        setTcStats((prev) => ({ ...prev, loading: false }));
      }
    }

    fetchTopLists();
  }, [fetchTopLists, period]);

  useEffect(() => {
    isMountedRef.current = true;
    setTcStats((prev) => ({ ...prev, loading: true }));
    fetchStatistics();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchStatistics, period]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  return (
    <React.Fragment>
      <Head title="Admin Dashboard" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Admin dashboard</BlockTitle>
              <BlockDes className="text-soft">
                <p>Inoqare platform overview</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <PeriodFilter period={period} onPeriodChange={handlePeriodChange} />
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            <Col md="6" lg="3">
              <TrendCard
                title="Users"
                value={stats.users.total}
                change={calculateChange(stats.users.total, stats.users.previous)}
                period="all time"
                trend={null}
                icon="users"
                color="bg-purple"
                link={getPublicUrl("user-list-compact")}
                loading={stats.users.loading}
              />
            </Col>
            <Col md="6" lg="3">
              <TrendCard
                title="Quotes"
                value={stats.quotes.total}
                change={calculateChange(stats.quotes.total, stats.quotes.previous)}
                period="all time"
                trend={null}
                icon="article"
                color="bg-teal"
                link={getPublicUrl("user-quotes")}
                loading={stats.quotes.loading}
              />
            </Col>
            <Col md="6" lg="3">
              <TrendCard
                title="Clinics"
                value={stats.clinics.total}
                change={calculateChange(stats.clinics.total, stats.clinics.previous)}
                period="all time"
                trend={null}
                icon="home"
                color="bg-info"
                link={getPublicUrl("clinics")}
                loading={stats.clinics.loading}
              />
            </Col>
            <Col md="6" lg="3">
              <TrendCard
                title="Medical partners"
                value={stats.partners.total}
                change={calculateChange(stats.partners.total, stats.partners.previous)}
                period="all time"
                trend={null}
                icon="plus-medi"
                color="bg-warning"
                link={getPublicUrl("medical-partner")}
                loading={stats.partners.loading}
              />
            </Col>
            <Col md="6" lg="3">
              <TrendCard
                title="Teleconsultations"
                value={stats.consultations.total}
                change={stats.consultations.previous > 0 ? calculateChange(stats.consultations.total, stats.consultations.previous) : null}
                period="total"
                trend={stats.consultations.previous > 0 && stats.consultations.total >= stats.consultations.previous ? "up" : "down"}
                icon="calendar"
                color="bg-primary"
                link={getPublicUrl("teleconsultations")}
                loading={stats.consultations.loading}
              />
            </Col>
          </Row>
        </Block>

        <Block>
          <PreviewAltCard className="card-bordered">
            <div className="card-title-group">
              <div className="card-title">
                <h6 className="title">Teleconsultation indicators</h6>
                <p className="sub-text text-soft">
                  Total and breakdown by status for the selected period. Link to full list below.
                </p>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => history.push(getPublicUrl("teleconsultations"))}
                  aria-label="View all teleconsultations"
                >
                  View all
                </button>
              </div>
            </div>
            {tcStats.loading ? (
              <p className="text-soft">Loading…</p>
            ) : (
              <Row className="g-gs mt-3">
                <Col md="6" lg="2">
                  <div className="p-3 bg-light rounded">
                    <span className="sub-text text-soft d-block">Total (period)</span>
                    <span className="h4 mb-0">{tcStats.total}</span>
                    {tcStats.previousTotal != null && tcStats.previousTotal > 0 && (
                      <span className="small text-soft">
                        {calculateChange(tcStats.total, tcStats.previousTotal) != null
                          ? `${tcStats.total >= tcStats.previousTotal ? "+" : ""}${calculateChange(tcStats.total, tcStats.previousTotal)}% vs previous period`
                          : "—"}
                      </span>
                    )}
                  </div>
                </Col>
                <Col md="6" lg="2">
                  <div className="p-3 bg-light rounded">
                    <span className="sub-text text-soft d-block">Scheduled</span>
                    <span className="h5 mb-0">{tcStats.byStatus.scheduled}</span>
                  </div>
                </Col>
                <Col md="6" lg="2">
                  <div className="p-3 bg-light rounded">
                    <span className="sub-text text-soft d-block">Paid</span>
                    <span className="h5 mb-0">{tcStats.byStatus.paid}</span>
                  </div>
                </Col>
                <Col md="6" lg="2">
                  <div className="p-3 bg-light rounded">
                    <span className="sub-text text-soft d-block">Completed</span>
                    <span className="h5 mb-0">{tcStats.byStatus.completed}</span>
                  </div>
                </Col>
                <Col md="6" lg="2">
                  <div className="p-3 bg-light rounded">
                    <span className="sub-text text-soft d-block">Cancelled</span>
                    <span className="h5 mb-0">{tcStats.byStatus.cancelled}</span>
                  </div>
                </Col>
                <Col md="6" lg="2">
                  <div className="p-3 bg-light rounded">
                    <span className="sub-text text-soft d-block">No-show</span>
                    <span className="h5 mb-0">{tcStats.byStatus.no_show}</span>
                  </div>
                </Col>
              </Row>
            )}
          </PreviewAltCard>
        </Block>

        <Block>
          <Row className="g-gs">
            <Col lg="6">
              <TopListWidget
                title="Clinics (first 5)"
                items={topClinics.items}
                metric={null}
                link="clinics"
                loading={topClinics.loading}
                maxItems={5}
                icon="home"
              />
            </Col>
            <Col lg="6">
              <TopListWidget
                title="Partners (first 5)"
                items={topPartners.items}
                metric={null}
                link="medical-partner"
                loading={topPartners.loading}
                maxItems={5}
                icon="plus-medi"
              />
            </Col>
          </Row>
        </Block>

        <Block>
          <Row className="g-gs">
            <Col lg="12">
              <PreviewAltCard className="card-bordered">
                <div className="card-title-group">
                  <div className="card-title">
                    <h6 className="title">Quick links</h6>
                    <p className="sub-text text-soft">
                      Shortcuts to main sections
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <Row className="g-3">
                    <Col md="6" lg="3">
                      <div className="sub-text text-soft">Users &amp; quotes</div>
                      <ul className="list-group list-group-flush mt-2">
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("user-list-compact")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("user-list-compact")); }}>Users</a>
                        </li>
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("user-quotes")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("user-quotes")); }}>Quotes</a>
                        </li>
                      </ul>
                    </Col>
                    <Col md="6" lg="3">
                      <div className="sub-text text-soft">Partners &amp; teleconsultation</div>
                      <ul className="list-group list-group-flush mt-2">
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("clinics")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("clinics")); }}>Clinics</a>
                        </li>
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("medical-partner")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("medical-partner")); }}>Medical partners</a>
                        </li>
                        <li className="list-group-item px-0">
                          <a
                            href={getPublicUrl("teleconsultations")}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push(getPublicUrl("teleconsultations"));
                            }}
                          >
                            Teleconsultations
                          </a>
                        </li>
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("teleconsultations/pending-partners")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("teleconsultations/pending-partners")); }}>TC partner approvals</a>
                        </li>
                      </ul>
                    </Col>
                    <Col md="6" lg="3">
                      <div className="sub-text text-soft">Finance &amp; configuration</div>
                      <ul className="list-group list-group-flush mt-2">
                        <li className="list-group-item px-0">
                          <a
                            href={getPublicUrl("invoice-list")}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push(getPublicUrl("invoice-list"));
                            }}
                          >
                            Invoices
                          </a>
                        </li>
                        <li className="list-group-item px-0">
                          <a
                            href={getPublicUrl("admin-settings")}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push(getPublicUrl("admin-settings"));
                            }}
                          >
                            Settings hub
                          </a>
                        </li>
                        <li className="list-group-item px-0">
                          <a
                            href={getPublicUrl("platform-catalog-roots")}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push(getPublicUrl("platform-catalog-roots"));
                            }}
                          >
                            Platform catalog roots
                          </a>
                        </li>
                      </ul>
                    </Col>
                    <Col md="6" lg="3">
                      <div className="sub-text text-soft">Other</div>
                      <ul className="list-group list-group-flush mt-2">
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("enterprise")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("enterprise")); }}>Enterprises</a>
                        </li>
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("contact-form")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("contact-form")); }}>Contact forms</a>
                        </li>
                        <li className="list-group-item px-0">
                          <a href={getPublicUrl("broker")} onClick={(e) => { e.preventDefault(); history.push(getPublicUrl("broker")); }}>Brokers</a>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </div>
              </PreviewAltCard>
            </Col>
          </Row>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default HomePage;
